import type { MouseEvent, ReactNode } from 'react';
import classNames from 'classnames';

import styles from './ToggleChip.module.scss';

export interface ToggleChipProps {
    children: ReactNode;
    /** Whether the filter is on. Leave it out for a chip that acts on the row, e.g. "All off". */
    pressed?: boolean;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    value?: string;
    name?: string;
    'aria-label'?: string;
    'aria-expanded'?: boolean;
    /** The element an `aria-expanded` chip shows and hides. */
    'aria-controls'?: string;
    /** Draws the chip dashed, for a chip that acts on the row (e.g. "+4") rather than filters it. */
    dashed?: boolean;
    className?: string;
}

/**
 * A filter chip: an on/off pill for picking any number of items from a row, such as weeks or
 * pens. The multi-select sibling of SegmentedControl: same selected colours, `aria-pressed`
 * instead of a tab role. A single on/off setting is a Switch, one choice out of several a
 * SegmentedControl.
 */
export function ToggleChip(props: ToggleChipProps) {
    const { children, pressed, onClick, disabled = false, dashed = false, className } = props;

    return (
        <button
            type="button"
            className={classNames(
                styles.chip,
                { [styles.pressed]: pressed, [styles.dashed]: dashed },
                className
            )}
            aria-pressed={pressed}
            aria-label={props['aria-label']}
            aria-expanded={props['aria-expanded']}
            aria-controls={props['aria-controls']}
            value={props.value}
            name={props.name}
            disabled={disabled}
            onClick={onClick}>
            {children}
        </button>
    );
}

export default ToggleChip;
